import { useEffect, useMemo, useState } from "react";
import PlatformManagerOrganizationsService, {
	type CreateOrUpdateOrganizationRequest,
	type Organization,
} from "@/lib/platformManager/platformManagerOrganization.service";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";

/**
 * This component is used to manage the organizations of the platform
 * @returns
 */
export const PlatformManagerOrganizationManagement = () => {
	const service = useMemo(
		() => PlatformManagerOrganizationsService.getInstance(),
		[],
	);

	const [organizations, setOrganizations] = useState<Organization[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isCreating, setIsCreating] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [showCreateDialog, setShowCreateDialog] = useState(false);

	const [name, setName] = useState("");
	const [description, setDescription] = useState("");

	/**
	 * This is called when the user wants to create a new organization
	 * @param e - The form event
	 */
	async function handleCreateOrganization(e: React.FormEvent) {
		e.preventDefault();
		if (!name.trim()) return;
		setIsCreating(true);
		setError(null);
		try {
			const payload: CreateOrUpdateOrganizationRequest = {
				name: name.trim(),
				description: description.trim() || undefined,
			};
			const created = await service.createOrganization(payload);
			setOrganizations((prev) => [created, ...prev]);
			setName("");
			setDescription("");
			setShowCreateDialog(false);
		} catch (_error) {
			setError("Fehler beim Erstellen der Organisation");
		} finally {
			setIsCreating(false);
		}
	}

	/**
	 * This is called when the component mounts
	 */
	useEffect(() => {
		let isMounted = true;
		(async () => {
			setIsLoading(true);
			setError(null);
			try {
				const data = await service.getAllOrganizations();
				if (isMounted) setOrganizations(data);
			} catch (_error) {
				if (isMounted) setError("Fehler beim Laden der Organisationen");
			} finally {
				if (isMounted) setIsLoading(false);
			}
		})();
		return () => {
			isMounted = false;
		};
	}, [service]);

	return (
		<div className="flex flex-col gap-6">
			<div className="flex items-center justify-between">
				<h2 className="text-xl font-semibold">Organisationen</h2>
				<Button onClick={() => setShowCreateDialog(true)}>
					Neue Organisation
				</Button>
			</div>

			<Card>
				<CardHeader>
					<CardTitle>Liste</CardTitle>
				</CardHeader>
				<CardContent>
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Name</TableHead>
								<TableHead>Beschreibung</TableHead>
								<TableHead>Users</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{isLoading ? (
								<TableRow>
									<TableCell colSpan={4}>Laden...</TableCell>
								</TableRow>
							) : organizations.length === 0 ? (
								<TableRow>
									<TableCell colSpan={4}>Keine Organisationen</TableCell>
								</TableRow>
							) : (
								organizations.map((org) => (
									<TableRow key={org.id}>
										<TableCell className="font-medium">{org.name}</TableCell>
										<TableCell className="text-muted-foreground">
											{org.description || "—"}
										</TableCell>
										<TableCell>{org.userCount ?? "—"}</TableCell>
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</CardContent>
			</Card>

			<Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
				<DialogContent className="sm:max-w-[500px] bg-background-secondary">
					<DialogHeader>
						<DialogTitle>Neue Organisation erstellen</DialogTitle>
						<DialogDescription>
							Gib die Details für die neue Organisation ein.
						</DialogDescription>
					</DialogHeader>
					<form onSubmit={handleCreateOrganization} className="space-y-3">
						<Input
							placeholder="Name der Organisation"
							value={name}
							onChange={(e) => setName(e.target.value)}
							required
						/>
						<Input
							placeholder="Beschreibung (optional)"
							value={description}
							onChange={(e) => setDescription(e.target.value)}
						/>
						{error ? (
							<div className="text-destructive text-sm">{error}</div>
						) : null}
						<DialogFooter>
							<Button
								variant="outline"
								type="button"
								onClick={() => setShowCreateDialog(false)}
								disabled={isCreating}
							>
								Abbrechen
							</Button>
							<Button type="submit" disabled={isCreating || !name.trim()}>
								{isCreating ? "Erstellen..." : "Erstellen"}
							</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>
		</div>
	);
};

export default PlatformManagerOrganizationManagement;
